<template>
  <v-container class="pa-0" fill-height fluid>
    <v-layout row wrap>
      <v-flex xs12>
        <v-list two-line>
          <v-list-tile
            :key="run.id"
            @click="['B', 'C', 'D', 'F'].includes(run.status) ? openRun(run.id) : ''"
            avatar
            v-for="run in myRuns"
          >
            <v-list-tile-avatar>
              <v-icon :class="status[run.status].iconClass">{{ status[run.status].icon }}</v-icon>
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title>{{ run.config.name }}</v-list-tile-title>
              <v-list-tile-sub-title>{{ run.updatedAt | moment("from", "now") }}</v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-menu @click.native.stop.prevent bottom left>
                <v-btn icon slot="activator">
                  <v-icon color="grey lighten-1">more_vert</v-icon>
                </v-btn>

                <v-list>
                  <v-list-tile
                    @click="terminateRun(run.id)"
                    v-if="run.status === 'S' || run.status === 'R' || run.status === 'Q'"
                  >
                    <v-list-tile-title>Terminate</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile :href="run.log.url" v-if="run.log.url">
                    <v-list-tile-title>Log</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile @click="copyRun(run.id)">
                    <v-list-tile-title>Copy</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="deleteRun(run.id)"
                    v-if="run.status !== 'S' && run.status !== 'R' && run.status !== 'Q'"
                  >
                    <v-list-tile-title>Delete</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
        <v-btn @click="createRun" bottom color="info" fab fixed right>
          <v-icon>add</v-icon>
        </v-btn>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
import defaultConfig from '@/config.run';
import { MY_RUNS, CREATE_RUN, COPY_RUN, DELETE_RUN, TERMINATE_RUN } from '@/graphql.gql';

export default {
  data() {
    return {
      status: {
        B: { icon: 'description', iconClass: 'grey white--text' },
        S: { icon: 'cloud_done', iconClass: 'info white--text' },
        Q: { icon: 'schedule', iconClass: 'yellow darken-3 white--text' },
        R: { icon: 'flash_on', iconClass: 'cyan white--text' },
        T: { icon: 'pause_circle_filled', iconClass: 'blue-grey darken-2 white--text' },
        C: { icon: 'cancel', iconClass: 'blue-grey white--text' },
        F: { icon: 'thumb_down', iconClass: 'error white--text' },
        D: { icon: 'thumb_up', iconClass: 'success white--text' },
      },
      myRuns: [],
    };
  },
  apollo: {
    myRuns: {
      query: MY_RUNS,
      update({ myRuns }) {
        myRuns.forEach((run) => {
          if (typeof run.config === 'string') {
            // eslint-disable-next-line no-param-reassign
            run.config = JSON.parse(run.config);
          }
        });
        return myRuns;
      },
      watchLoading(isLoading) {
        if (isLoading && !this.$wait.is('busy')) {
          this.$wait.start('busy');
        } else if (!isLoading && this.$wait.is('busy')) {
          this.$wait.end('busy');
        }
      },
    },
  },
  methods: {
    openRun(runId) {
      this.$router.push({ name: 'run', params: { id: runId } });
    },
    async createRun() {
      try {
        this.$wait.start('busy');
        await this.$apollo.mutate({
          mutation: CREATE_RUN,
          variables: { config: JSON.stringify(defaultConfig) },
          update: (store, { data: { createRun } }) => {
            const data = store.readQuery({ query: MY_RUNS });
            data.myRuns.push(createRun);
            store.writeQuery({ query: MY_RUNS, data });
            this.$router.push({ name: 'run', params: { id: createRun.id } });
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.$snackbar.show('We could not create a new run. Please, try again.');
      } finally {
        this.$wait.end('busy');
      }
    },
    async copyRun(id) {
      try {
        this.$wait.start('busy');
        await this.$apollo.mutate({
          mutation: COPY_RUN,
          variables: { id },
          update: (store, { data: { copyRun } }) => {
            const data = store.readQuery({ query: MY_RUNS });
            data.myRuns.push(copyRun);
            store.writeQuery({ query: MY_RUNS, data });
            this.$router.push({ name: 'run', params: { id: copyRun.id } });
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.$snackbar.show('We could not copy this run. Please, try again.');
      } finally {
        this.$wait.end('busy');
      }
    },
    async deleteRun(id) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_RUN,
          variables: { id },
          update: (store, { data: { deleteRun } }) => {
            const data = store.readQuery({ query: MY_RUNS });
            _.remove(data.myRuns, (obj) => obj.id === deleteRun.id);
            store.writeQuery({ query: MY_RUNS, data });
          },
          optimisticResponse: {
            __typename: 'Mutation',
            deleteRun: {
              __typename: 'DeleteRun',
              id,
            },
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.$snackbar.show('We could not delete this run. Please, try again.');
      }
    },
    async terminateRun(id) {
      try {
        await this.$apollo.mutate({
          mutation: TERMINATE_RUN,
          variables: { id },
          update: (store, { data: { terminateRun } }) => {
            const data = store.readQuery({ query: MY_RUNS });
            const run = _.find(data.myRuns, { id: terminateRun.id });
            run.status = terminateRun.status;
            store.writeQuery({ query: MY_RUNS, data });
          },
          optimisticResponse: {
            __typename: 'Mutation',
            terminateRun: {
              __typename: 'RunType',
              id,
              status: 'T',
            },
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.$snackbar.show('We could not terminate this run. Please, try again.');
      }
    },
  },
  created() {
    this.$store.dispatch('setTitle', 'Runs');
  },
};
</script>
